import React, { ReactNode, useMemo, useState } from 'react'
import {
    Box,
    ButtonBase,
    Card,
    CardContent,
    Chip,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined'
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined'
import { HttpSystemsNewByGroup } from 'src/http/group/httpSystemsNewByGroup'
import { EntityWithIdAndDescriptionFields, EntityWithIdFields } from 'src/types'
import { SistemaTipo } from 'src/types/enum/enum'
import useSecurityObject from 'src/hooks/useSecurityObject'
import { LimitesSecObjects } from 'src/types/security'
import Dashboard from '../dashboard/Dashboard'
import Visualizador from '../grafo/Visualizador'
import LimitesCLG from './CLG/LimitesCLG'
import LimitesCLFB from './CLFB/LimitesCLFB'
import LimitesContraparte from './contraparte/LimitesContraparte'
import { LimitesLCTC } from './LCTC/LimitesLCTC'
import { LimitesLCLS } from './LCLS/LimitesLCLS'

type LimitesMenuKey =
    | 'dashboard'
    | 'visualizador'
    | 'limites-credit-limit-garantias'
    | 'limites-credit-limit-facilitation-book'
    | 'limites-contraparte'
    | 'limites-lcls'
    | 'limites-lctc'

type LimitesMenuItem = {
    key: LimitesMenuKey
    title: string
    description: string
    group?: string
    sistemaTipo?: SistemaTipo
    securityObject?: LimitesSecObjects
    icon: ReactNode
}

type MenuSectionStyle = {
    color: string
    bgcolor: string
}

const limitesMenuItems: LimitesMenuItem[] = [
    {
        key: 'limites-credit-limit-garantias',
        title: 'Credit Limit Garantias',
        description: 'Aforos aprobados',
        group: 'Limites',
        sistemaTipo: SistemaTipo.CLG,
        securityObject: LimitesSecObjects.LinkLimitesCreditLimitGarantias,
        icon: <QueryStatsOutlinedIcon fontSize='small' />
    },
    {
        key: 'limites-credit-limit-facilitation-book',
        title: 'Credit Limit Facilitation Book',
        description: 'Limites aprobados',
        group: 'Limites',
        sistemaTipo: SistemaTipo.CLFB,
        securityObject: LimitesSecObjects.LinkLimitesCreditLimitFacilitationBook,
        icon: <QueryStatsOutlinedIcon fontSize='small' />
    },
    {
        key: 'limites-contraparte',
        title: 'Contraparte',
        description: 'Limites por cuenta',
        group: 'Limites',
        sistemaTipo: SistemaTipo.Contrapartes,
        securityObject: LimitesSecObjects.LinkLimitesContraparte,
        icon: <QueryStatsOutlinedIcon fontSize='small' />
    },
    {
        key: 'limites-lcls',
        title: 'LCLS',
        description: 'Limites por cuenta',
        group: 'Limites',
        sistemaTipo: SistemaTipo.LCLS,
        securityObject: LimitesSecObjects.LinkLimitesLCLS,
        icon: <QueryStatsOutlinedIcon fontSize='small' />
    },
    {
        key: 'limites-lctc',
        title: 'LCTC',
        description: 'Limites por cliente',
        group: 'Limites',
        sistemaTipo: SistemaTipo.LCTC,
        securityObject: LimitesSecObjects.LinkLimitesLCTC,
        icon: <QueryStatsOutlinedIcon fontSize='small' />
    },
    {
        key: 'dashboard',
        title: 'Dashboard',
        description: 'Resumen operativo',
        icon: <DashboardOutlinedIcon fontSize='small' />
    },
    {
        key: 'visualizador',
        title: 'Visualizador',
        description: 'Consultas graficas',
        icon: <InsertChartOutlinedIcon fontSize='small' />
    }
]

function Limites(): JSX.Element {
    const [activeItem, setActiveItem] = useState<LimitesMenuKey>('limites-credit-limit-garantias')
    const [limitesOpen, setLimitesOpen] = useState(true)
    const [menuOpen, setMenuOpen] = useState(true)
    const [sistemaTipoTitulos, setSistemaTipoTitulos] = useState<Record<number, string>>({})
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
    const { dictionarySecurityObject, hasReadPermission } = useSecurityObject()

    const canReadLimitItem = (item: LimitesMenuItem) =>
        !item.securityObject || !!dictionarySecurityObject?.[item.securityObject] && hasReadPermission(item.securityObject)

    const limiteItems = useMemo(
        () => limitesMenuItems.filter(item => item.group === 'Limites' && canReadLimitItem(item)),
        [dictionarySecurityObject]
    )
    const directItems = useMemo(() => limitesMenuItems.filter(item => !item.group), [])
    const visibleItems = useMemo(() => [...limiteItems, ...directItems], [limiteItems, directItems])

    const currentItem = visibleItems.find(item => item.key === activeItem)
    const getItemTitle = (item: LimitesMenuItem) =>
        item.sistemaTipo ? sistemaTipoTitulos[item.sistemaTipo] ?? item.title : item.title
    const getSectionStyle = (selected: boolean, color: string): MenuSectionStyle => ({
        color: selected ? color : 'text.secondary',
        bgcolor: selected ? alpha(color, 0.12) : alpha(theme.palette.text.primary, 0.04)
    })

    React.useEffect(() => {
        HttpSystemsNewByGroup.getSystemTypes()
            .then(sistemasTipo => {
                setSistemaTipoTitulos(
                    sistemasTipo.reduce<Record<number, string>>((acc, sistemaTipo) => {
                        acc[sistemaTipo[EntityWithIdFields.Id]] = sistemaTipo[EntityWithIdAndDescriptionFields.Description]

                        return acc
                    }, {})
                )
            })
            .catch(error => {
                console.error('Error al obtener tipos de sistema para limites:', error)
            })
    }, [])

    React.useEffect(() => {
        if (!dictionarySecurityObject || !visibleItems.length) {
            return
        }

        if (!visibleItems.some(item => item.key === activeItem)) {
            setActiveItem(visibleItems[0].key)
        }
    }, [activeItem, dictionarySecurityObject, visibleItems])

    const renderContent = () => {
        if (!currentItem) {
            return null
        }

        switch (currentItem.key) {
            case 'limites-credit-limit-garantias':
                return <LimitesCLG />
            case 'limites-credit-limit-facilitation-book':
                return <LimitesCLFB />
            case 'limites-contraparte':
                return <LimitesContraparte />
            case 'limites-lcls':
                return <LimitesLCLS />
            case 'limites-lctc':
                return <LimitesLCTC />
            case 'visualizador':
                return <Visualizador />
            case 'dashboard':
            default:
                return <Dashboard />
        }
    }

    const handleSelectItem = (itemKey: LimitesMenuKey) => {
        setActiveItem(itemKey)

        if (itemKey === 'visualizador') {
            setMenuOpen(false)
        }
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Grid container spacing={4} alignItems='stretch'>
                <Grid item xs={12} md={menuOpen ? 3 : 0.6} lg={menuOpen ? 2.5 : 0.45}>
                    <Card
                        sx={{
                            height: { xs: 'auto', md: 'calc(100vh - 180px)' },
                            position: { md: 'sticky' },
                            top: { md: 88 },
                            overflow: 'hidden'
                        }}
                    >
                        <CardContent sx={{ p: menuOpen ? 3 : 1.5 }}>
                            <Stack spacing={3}>
                                <ButtonBase
                                    onClick={() => setMenuOpen(open => !open)}
                                    sx={{
                                        width: '100%',
                                        justifyContent: 'center',
                                        borderRadius: 1,
                                        p: 1,
                                        color: 'text.secondary',
                                        bgcolor: 'action.hover',
                                        '&:hover': {
                                            bgcolor: 'action.selected'
                                        }
                                    }}
                                >
                                    {menuOpen ? <ChevronLeftIcon fontSize='small' /> : <ChevronRightIcon fontSize='small' />}
                                </ButtonBase>

                                {menuOpen && (
                                <Stack
                                    spacing={3}
                                    direction={isSmallScreen ? 'row' : 'column'}
                                    sx={{
                                        overflowX: isSmallScreen ? 'auto' : 'visible',
                                        pb: isSmallScreen ? 1 : 0
                                    }}
                                >
                                    <Stack spacing={1} sx={{ minWidth: isSmallScreen ? 260 : 'auto' }}>
                                        <ButtonBase
                                            onClick={() => setLimitesOpen(open => !open)}
                                            sx={{
                                                width: '100%',
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                                borderRadius: 1,
                                                p: 2,
                                                color: 'primary.main',
                                                bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                                                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.45)}`,
                                                borderLeft: theme => `5px solid ${theme.palette.primary.main}`,
                                                '&:hover': {
                                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.16)
                                                }
                                            }}
                                        >
                                            <Stack direction='row' spacing={1.5} alignItems='center' justifyContent='space-between' width='100%'>
                                                <Stack direction='row' spacing={1.5} alignItems='center'>
                                                    <QueryStatsOutlinedIcon fontSize='small' />
                                                    <Typography variant='body2' fontWeight={700}>
                                                        Limites
                                                    </Typography>
                                                </Stack>
                                                <ExpandMoreIcon
                                                    fontSize='small'
                                                    sx={{
                                                        transform: limitesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: theme => theme.transitions.create('transform')
                                                    }}
                                                />
                                            </Stack>
                                        </ButtonBase>
                                        {limitesOpen && limiteItems.map(item => {
                                            const selected = item.key === activeItem

                                            return (
                                                <ButtonBase
                                                    key={item.key}
                                                    onClick={() => handleSelectItem(item.key)}
                                                    sx={{
                                                        width: '100%',
                                                        justifyContent: 'flex-start',
                                                        textAlign: 'left',
                                                        borderRadius: 1,
                                                        py: 1.5,
                                                        pr: 2,
                                                        pl: 4,
                                                        color: selected ? 'primary.main' : 'text.primary',
                                                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                                        border: theme =>
                                                            `1px solid ${
                                                                selected ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider
                                                            }`,
                                                        borderLeft: theme =>
                                                            `3px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                                                        '&:hover': {
                                                            bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : 'action.hover'
                                                        }
                                                    }}
                                                >
                                                    <Stack direction='row' spacing={2} alignItems='center' width='100%'>
                                                        {item.icon}
                                                        <Box>
                                                            <Typography variant='body2' fontWeight={600}>
                                                                {getItemTitle(item)}
                                                            </Typography>
                                                            <Typography variant='caption' color='text.secondary'>
                                                                {item.description}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </ButtonBase>
                                            )
                                        })}
                                    </Stack>

                                    {directItems.map(item => {
                                        const selected = item.key === activeItem
                                        const isDashboard = item.key === 'dashboard'
                                        const sectionColor = isDashboard ? theme.palette.success.main : theme.palette.info.main
                                        const sectionStyle = getSectionStyle(true, sectionColor)

                                        return (
                                            <Stack key={item.key} spacing={1} sx={{ minWidth: isSmallScreen ? 220 : 'auto' }}>
                                            <ButtonBase
                                                onClick={() => handleSelectItem(item.key)}
                                                sx={{
                                                    width: '100%',
                                                    justifyContent: 'flex-start',
                                                    textAlign: 'left',
                                                    borderRadius: 1,
                                                    p: 2,
                                                    color: sectionStyle.color,
                                                    bgcolor: sectionStyle.bgcolor,
                                                    border: `1px solid ${alpha(sectionColor, selected ? 0.65 : 0.45)}`,
                                                    borderLeft: `5px solid ${sectionColor}`,
                                                    '&:hover': {
                                                        bgcolor: alpha(sectionColor, selected ? 0.14 : 0.08)
                                                    }
                                                }}
                                            >
                                                <Stack direction='row' spacing={2} alignItems='center' width='100%'>
                                                    {item.icon}
                                                    <Box>
                                                        <Typography variant='body2' fontWeight={600}>
                                                            {getItemTitle(item)}
                                                        </Typography>
                                                        <Typography variant='caption' color='text.secondary'>
                                                            {item.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </ButtonBase>
                                        </Stack>
                                        )
                                    })}
                                </Stack>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={menuOpen ? 9 : 11.4} lg={menuOpen ? 9.5 : 11.55}>
                    <Stack spacing={4}>
                        {currentItem && currentItem.key !== 'visualizador' && (
                            <Card>
                                <CardContent>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={2}
                                        justifyContent='space-between'
                                    >
                                        <Box>
                                            <Typography variant='h5'>{getItemTitle(currentItem)}</Typography>
                                            <Typography color='text.secondary'>{currentItem.description}</Typography>
                                        </Box>
                                        <Chip
                                            label={currentItem.group ?? 'Informacion'}
                                            color='primary'
                                            variant='outlined'
                                            sx={{ borderRadius: 1, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {renderContent()}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    )
}

export default Limites
